import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  ListGroup,
  Button,
  Card,
  Spinner,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import { FaTags, FaChevronRight, FaSearch } from "react-icons/fa";
import { Link } from "react-router-dom";
import "./AllProducts.css";

const AllProducts = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [zoomedImage, setZoomedImage] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get("https://agpipefitting.org.et/api/categories");
        // Ensure categories is always an array
        const categoriesData = Array.isArray(response.data) ? response.data : [];
        setCategories(categoriesData);
        setSelectedCategory(categoriesData[0]?.id || null);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
        };
        if (selectedCategory) params.category_id = selectedCategory;
        if (searchQuery) params.search = searchQuery;

        console.log('Fetching products with params:', params);
        
        const response = await axios.get(
          "https://agpipefitting.org.et/api/product_descriptions",
          { params }
        );

        console.log('Full API Response:', response);
        console.log('Response data:', response.data);

        let productsData = [];
        let paginationData = { current_page: 1, last_page: 1 };

        // Comprehensive response structure checking
        if (response.data) {
          // Check various possible response structures
          if (Array.isArray(response.data)) {
            // Case 1: Direct array
            productsData = response.data;
          } else if (response.data.data !== undefined) {
            // Case 2: Has data property
            if (Array.isArray(response.data.data)) {
              productsData = response.data.data;
            } else if (response.data.data && typeof response.data.data === 'object') {
              // If data is an object, try to extract array from it
              const dataObj = response.data.data;
              const possibleArrays = Object.values(dataObj).find(val => Array.isArray(val));
              if (possibleArrays) {
                productsData = possibleArrays;
              }
            }
          } else if (response.data.products && Array.isArray(response.data.products)) {
            // Case 3: Has products array
            productsData = response.data.products;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            // Case 4: Has items array
            productsData = response.data.items;
          } else {
            // Case 5: Try to find any array in the response
            const allValues = Object.values(response.data);
            const foundArray = allValues.find(val => Array.isArray(val));
            if (foundArray) {
              productsData = foundArray;
            }
          }

          // Handle pagination
          if (response.data.meta) {
            paginationData = response.data.meta;
          } else if (response.data.pagination) {
            paginationData = response.data.pagination;
          } else if (response.data.current_page !== undefined) {
            paginationData = {
              current_page: response.data.current_page,
              last_page: response.data.last_page || response.data.total_pages || 1
            };
          }
        }

        console.log('Extracted products data:', productsData);
        console.log('Extracted pagination data:', paginationData);

        // Final safety check - ensure productsData is an array
        if (!Array.isArray(productsData)) {
          console.warn('Products data is not an array, converting to empty array:', productsData);
          productsData = [];
        }

        setProducts(productsData);
        setCurrentPage(paginationData.current_page || 1);
        setLastPage(paginationData.last_page || paginationData.total_pages || 1);
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
        setCurrentPage(1);
        setLastPage(1);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory, searchQuery, currentPage]);

  const handleImageLoad = (productId) => {
    setLoadedImages((prev) => ({ ...prev, [productId]: true }));
  };

  const handleImageClick = (imageUrl) => {
    setZoomedImage(imageUrl);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (lastPage <= 1) return null;
    
    const pages = [];
    for (let page = 1; page <= lastPage; page++) {
      pages.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    return <Pagination>{pages}</Pagination>;
  };

  // Safe render functions
  const renderCategories = () => {
    if (!Array.isArray(categories)) return null;
    
    return categories.map((category) => (
      <ListGroup.Item
        key={category.id}
        action
        onClick={() => {
          setSelectedCategory(category.id);
          setCurrentPage(1);
        }}
        className={`d-flex justify-content-between align-items-center small py-2 px-3 ${
          selectedCategory === category.id ? "active-category" : ""
        }`}
      >
        <span>{category.name}</span>
        <FaChevronRight className="text-muted ms-1" />
      </ListGroup.Item>
    ));
  };

  const renderProducts = () => {
    if (!Array.isArray(products)) {
      console.error('Products is not an array:', products);
      return null;
    }
    
    if (products.length === 0) return null;

    return products.map((product) => (
      <Col key={product.id}>
        <Card className="h-100 border-0 shadow-sm product-card">
          <div
            onClick={() => handleImageClick(`https://agpipefitting.org.et/storage/${product.image}`)}
            style={{ cursor: "pointer", height: "180px", overflow: "hidden" }}
          >
            <Card.Img
              variant="top"
              src={`https://agpipefitting.org.et/storage/${product.image}`}
              onLoad={() => handleImageLoad(product.id)}
              onError={(e) => {
                e.target.src = "/placeholder-product.jpg";
                handleImageLoad(product.id);
              }}
              className={`object-fit-cover ${loadedImages[product.id] ? "loaded" : "loading"}`}
              loading="lazy"
              style={{
                objectFit: "cover",
                width: "100%",
                height: "100%",
                paddingTop: "10px",
                paddingRight: "0",
                paddingLeft: "0",
              }}
            />
          </div>
          <Card.Body className="p-3 d-flex flex-column">
            <h6 className="text-secondary mb-2">
              {product.category?.name || "Product"}
            </h6>
            <Link
              to={`/products/${product.id}`}
              className="mt-auto btn btn-sm btn-outline-primary w-100"
            >
              View Details
            </Link>
          </Card.Body>
        </Card>
      </Col>
    ));
  };

  return (
    <div className="container-fluid px-lg-4 py-5">
      {/* Hero Section */}
      <header className="text-center mb-5 position-relative">
        <div className="hero-section bg-dark text-white py-5 rounded-4 shadow-sm position-relative overflow-hidden">
          <div
            className="position-absolute top-0 start-0 w-100 h-100 opacity-25"
            style={{
              backgroundImage: "url('/hero-bg.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          ></div>
          <div className="position-relative z-2">
            <h1 className="display-4 fw-bold">AG Pipe Fitting Technology</h1>
            <p className="lead mb-0">Premium Plumbing Solutions Since 1998</p>
          </div>
        </div>
      </header>

      <Row className="g-4">
        {/* Categories Sidebar */}
        <Col lg={3} className="d-none d-lg-block">
          <div className="fixed-sidebar">
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Header className="py-3 bg-primary text-white border-0">
                <h2 className="h6 mb-0 d-flex align-items-center">
                  <FaTags className="me-2" />
                  Categories
                </h2>
              </Card.Header>
              <ListGroup variant="flush" className="category-list">
                {renderCategories()}
              </ListGroup>
            </Card>
          </div>
        </Col>

        {/* Products Main Area */}
        <Col lg={9}>
          <div className="ps-lg-4">
            {/* Search & Header */}
            <Row className="mb-4 align-items-center g-2">
              <Col xs={12} md={6}>
                <h2 className="h5 fw-bold text-dark mb-0">
                  {selectedCategory
                    ? (Array.isArray(categories) ? categories.find(c => c.id === selectedCategory)?.name : "") + " Products"
                    : "All Products"}
                </h2>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="search-bar mb-0">
                  <div className="input-group">
                    <Form.Control
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="rounded-start"
                    />
                    <Button variant="primary" className="rounded-end px-3">
                      <FaSearch />
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Content Area */}
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2">Loading Products...</p>
                </div>
              </div>
            ) : Array.isArray(products) && products.length > 0 ? (
              <>
                <Row xs={2} md={3} xl={4} className="g-3">
                  {renderProducts()}
                </Row>

                {/* Pagination */}
                <div className="d-flex justify-content-center mt-4">
                  {renderPagination()}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <img
                  src="/empty-state.svg"
                  alt="No products"
                  className="mb-4"
                  style={{ width: "180px" }}
                />
                <h5 className="text-muted">No products found</h5>
                <p className="text-secondary">
                  Try a different search or category.
                </p>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                >
                  Reset Filters
                </Button>
              </div>
            )}

            {/* Image Zoom Overlay */}
            {zoomedImage && (
              <div
                className="product-image-zoom d-flex justify-content-center align-items-center"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.8)",
                  zIndex: 1050,
                  cursor: "zoom-out",
                }}
                onClick={() => setZoomedImage(null)}
              >
                <img
                  src={zoomedImage}
                  alt="Zoomed"
                  style={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    borderRadius: "8px",
                  }}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AllProducts;